import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps } from '../../utils/buildingSelection';
import { apartmentsApi, type Building, type CreateApartmentGroupData } from '../../api/apartmentsApi';

interface AddApartmentGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedBuildingId?: string;
}

const AddApartmentGroupModal: React.FC<AddApartmentGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedBuildingId
}) => {  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  
  const [formData, setFormData] = useState<CreateApartmentGroupData>({
    name: '',
    description: '',
    buildingId: selectedBuildingId || ''
  });

  useEffect(() => {
    if (isOpen) {
      loadBuildings();
      setFormData(prev => ({
        ...prev,
        buildingId: selectedBuildingId || prev.buildingId
      }));
    }
  }, [isOpen, selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setFormData((prev) => ({
        ...prev,
        buildingId: applyDefaultBuildingSelection(data, user, prev.buildingId || selectedBuildingId || ''),
      }));
    } catch (err) {
      console.error('Failed to load buildings:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Vui lòng nhập tên nhóm căn hộ');
      return;
    }
    if (!formData.buildingId) {
      setError('Vui lòng chọn chung cư');
      return;
    }

    try {
      setLoading(true);
      await apartmentsApi.createApartmentGroup(formData);
      setFormData({ name: '', description: '', buildingId: selectedBuildingId || '' });
      onSuccess();
      onClose();
    } catch (err) {
      setError('Lỗi khi tạo nhóm căn hộ');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-red-600">Thêm nhóm căn hộ</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Building Selection */}
          {shouldShowBuildingSelector(buildings) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chọn chung cư
            </label>
            <select
              name="buildingId"
              value={formData.buildingId}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-700"
              required
              disabled={!needsBuildingSelection(buildings)}
            >
              <option value="">-- Chọn chung cư --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
          )}

          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tên nhóm căn hộ
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ví dụ: Tòa S1"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ghi chú:
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Ví dụ: Nhóm cho các căn hộ Tòa S1"
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Đóng
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddApartmentGroupModal; 